import { useState, useEffect } from 'react';
import { agencyApi, AgencyEmail } from '../../utils/agencyApi';

export function useAuthorizedReportersData() {
  const [emails, setEmails] = useState<AgencyEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addEmail, setAddEmail] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addWarning, setAddWarning] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadEmails = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await agencyApi.getEmails();
      setEmails(res.emails ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load authorized reporters');
    } finally {
      setLoading(false);
    }
  };

  // loadEmails only closes over stable useState setters, so [] is safe
  useEffect(() => { loadEmails(); }, []);

  const handleAdd = async () => {
    setAddError(null);
    setAddWarning(null);
    setAddLoading(true);
    try {
      const res = await agencyApi.addEmail(addEmail);
      if (res.success) {
        setAddEmail('');
        if (res.warning) setAddWarning(res.warning);
        await loadEmails();
        setSnackbar('Reporter added');
      }
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add email');
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async (email: string) => {
    setRemoveLoading(true);
    try {
      await agencyApi.removeEmail(email);
      await loadEmails();
      setSnackbar('Reporter removed');
    } catch {
      setSnackbar('Failed to remove reporter');
    } finally {
      setRemoveLoading(false);
    }
  };

  return {
    emails, loading, error,
    addEmail, setAddEmail, addLoading, addError, addWarning,
    handleAdd, handleRemove,
    mutating: addLoading || removeLoading,
    snackbar, setSnackbar,
  };
}
